import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useMatch, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { usePipedriveStore, usePopupStore, useViewSettingsStore } from "../..";
import useActivePage from "../../hooks/useActivePage";
import useDeals, { DealsSortingType } from "../../hooks/useDeals";
import useDealsOwnerFilterValues from "../../hooks/useDealsOwnerFilterValues";
import useDealsWithFocusArea from "../../hooks/useDealsWithFocusArea";
import useDealsWithGlobalFilters from "../../hooks/useDealsWithGlobalFilters";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import usePagination from "../../hooks/usePagination";
import PopupType from "../../interfaces/PopupType";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import DealInfo from "./DealInfo";

function Deals() {
    const { id } = useParams();
    const isDealPage = useMatch("/deals/*");
    const navigateToDeal = useNavigateToDeal();
    const pipedriveStore = usePipedriveStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const ownerFilterValues = useDealsOwnerFilterValues();
    const [ownerFilter, setOwnerFilter] = useState(ownerFilterValues[0]);
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(DealsSortingType.CreatedNewest);
    const [page, setPage] = useState(1);
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(useDeals(ownerFilter, debouncedSearchFilter, sortBy)));
    const [pageCount, pagedDeals] = usePagination(deals, page, () => setPage(1));
    useActivePage(deals, Number(id), isDealPage && id && deals.find(d => d.id === Number(id)), searchFilter, ownerFilter, sortBy, setPage);

    useEffect(() => {
        if (!ownerFilterValues.includes(ownerFilter))
            setOwnerFilter(ownerFilterValues[0]);
    }, [ownerFilterValues, ownerFilter]);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!pipedriveStore.isDealsLoading.fetching && formatNumber(deals.length)} Deals
                {!viewSettingsStore.isMobile && <GlobalFiltersIcon tab={GlobalFiltersTab.Deals} className={tabStyles.headerButton} />}
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus={!viewSettingsStore.isMobile} placeholder="Search deals.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                        {ownerFilterValues.map(v => <option key={v}>{v}</option>)}
                    </select>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={DealsSortingType.NameAlphabet}>Name (A - Z)</option>
                        <option value={DealsSortingType.NameReverseAlphabet}>Name (Z - A)</option>
                        <option value={DealsSortingType.PriceLowToHigh}>Price ($ - $$$)</option>
                        <option value={DealsSortingType.PriceHighToLow}>Price ($$$ - $)</option>
                        <option value={DealsSortingType.CreatedNewest}>Newest</option>
                        <option value={DealsSortingType.CreatedOldest}>Oldest</option>
                        <option value={DealsSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={DealsSortingType.ModifiedOldest}>Modified (Oldest)</option>
                        <option value={DealsSortingType.StageEarliest}>Stage (Earliest)</option>
                        <option value={DealsSortingType.StageLatest}>Stage (Latest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {pipedriveStore.isDealsLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedDeals.map(d => (
                        <DealInfo
                            key={d.id}
                            className={[
                                tabStyles.item,
                                isDealPage && id === d.id.toString()
                                    ? tabStyles.opened
                                    : popupStore.type === PopupType.Deal && popupStore.id === d.id
                                        ? tabStyles.selected
                                        : "",
                            ].join(" ")}
                            name={d.name}
                            value={d.value}
                            status={d.status}
                            stageId={d.dealStageId}
                            buildingSize={d.buildingSize}
                            landSizeAcres={d.landSizeAcres}
                            zoning={d.zoning}
                            onClick={() => navigateToDeal(d.id)}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    )
}

export default observer(Deals);